import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { PROJECT_DETAILS_FIELDS_UPDATED, ProjectDetailsField } from './projectDetailsFieldsService';
import { ProjectDetailsFieldType, ProjectType } from './Types';
import { Plan } from './plansService';

export interface CreateGlobalDetailsFieldRequest {
  name: string;
  fieldType: ProjectDetailsFieldType;
  order: number;
  groupId?: number;
  projectTypes: ProjectType[];
}

export interface UpdateGlobalDetailsFieldRequest {
  name: string;
  fieldType: ProjectDetailsFieldType;
  projectTypes: ProjectType[];
  groupId?: number;
}

export interface ProjectDetailsFilter {
  projectType?: ProjectType;
}

export const ADMIN_DETAILS_FIELDS_UPDATED = 'ADMIN_DETAILS_FIELDS_UPDATED';

function filterFields(
  fields: ProjectDetailsField[],
  filter?: ProjectDetailsFilter
): ProjectDetailsField[] {
  const projectType = filter?.projectType;
  if (projectType) {
    return fields.filter(field => {
      if (field.projectTypes && field.projectTypes.length > 0) {
        return field.projectTypes.includes(projectType);
      }
      return true;
    });
  } else {
    return fields;
  }
}

export function useProjectDetailsFieldsAdminService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const createField = async (
    areaId: number,
    version: string,
    request: CreateGlobalDetailsFieldRequest
  ): Promise<ProjectDetailsField> => {
    const result = await nekstApi.post(`/manager/setupareas/${areaId}/${version}/detailsfields`, request) as ProjectDetailsField;
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {});
    return result;
  };

  const getAreaFields = async (
    areaId: number,
    version: string,
    filter?: ProjectDetailsFilter
  ): Promise<ProjectDetailsField[]> => {
    if (filter?.projectType) {
      return await nekstApi.get(`/manager/setupareas/${areaId}/${version}/detailsfields/${filter.projectType.toLowerCase()}`) as ProjectDetailsField[];
    } else {
      return await nekstApi.get(`/manager/setupareas/${areaId}/${version}/detailsfields`) as ProjectDetailsField[];
    }
  };

  const updateField = async (
    fieldId: number,
    request: UpdateGlobalDetailsFieldRequest
  ): Promise<ProjectDetailsField> => {
    const result = await nekstApi.put(`/manager/detailsfields/${fieldId}`, request) as ProjectDetailsField;
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {});
    return result;
  };

  const deleteField = async (
    fieldId: number
  ): Promise<void> => {
    await nekstApi.delete(`/manager/detailsfields/${fieldId}`);
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {});
  };

  const removeFieldFromSetupAreaVersion = async (
    areaId: number,
    version: string,
    fieldId: number
  ): Promise<void> => {
    await nekstApi.delete(`/manager/setupareas/${areaId}/${version}/detailsfields/${fieldId}`);
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
  }

  const getAllFields = async (): Promise<ProjectDetailsField[]> => {
    return await nekstApi.get('/manager/detailsfields') as ProjectDetailsField[];
  };

  const setOrder = async (areaId: number, version: string, ids: number[]): Promise<void> => {
    await nekstApi.post(`/manager/setupareas/${areaId}/${version}/detailsfields/order`, { ids });
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
  };

  const copyFields = async (setupAreaId: number, setupAreaVersion: string, ids: number[]) => {
    await nekstApi.post('/manager/detailsfields/copy', {
      ids: ids,
      setupAreaId: setupAreaId,
      setupAreaVersion: setupAreaVersion
    });
  };

  const setStartAndEndDates = async (
    setupAreaId: number,
    setupAreaVersion: string,
    projectType: ProjectType,
    startDateFieldId?: number,
    endDateFieldId?: number
  ) => {
    await nekstApi.put(`/manager/setupareas/${setupAreaId}/${setupAreaVersion}/detailsfields/${projectType.toLowerCase()}/dates`, {
      startDateFieldId,
      endDateFieldId
    });
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
  };

  const setClosingDateField = async (
    setupAreaId: number,
    setupAreaVersion: string,
    projectType: ProjectType,
    closingDateFieldId?: number
  ): Promise<void> => {
    await nekstApi.post(`/manager/setupareas/${setupAreaId}/${setupAreaVersion}/detailsfields/${projectType.toLowerCase()}/closingdate`, {
      closingDateFieldId
    });
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
  };

  const setCommissionField = async (
    setupAreaId: number,
    setupAreaVersion: string,
    projectType: ProjectType,
    commissionFieldId?: number
  ): Promise<void> => {
    await nekstApi.post(`/manager/setupareas/${setupAreaId}/${setupAreaVersion}/detailsfields/${projectType.toLowerCase()}/commission`, {
      commissionFieldId
    });
    eventsPublisher.publish(ADMIN_DETAILS_FIELDS_UPDATED, {});
  };

  const createPlanFromFields = async (
    setupAreaId: number,
    setupAreaVersion: string,
    projectType: ProjectType,
    name: string
  ) => {
    return await nekstApi.post(
      `/manager/setupareas/${setupAreaId}/${setupAreaVersion}/detailsfields/${projectType.toLowerCase()}/plan`,
      { name }
    ) as Plan;
  }

  return {
    createField,
    getAreaFields,
    updateField,
    deleteField,
    removeFieldFromSetupAreaVersion,
    getAllFields,
    setOrder,
    copyFields,
    setStartAndEndDates,
    setClosingDateField,
    setCommissionField,
    createPlanFromFields,
  };
}
